import RootStore from './RootStore';
import { IResult } from '../global.d';
import { observable, action, computed } from 'mobx';

import voteApi from '../actions/vote';
import stat from '../utils/stat';

export interface IVoteOption {
  image?: string;
  name: string;
  value: string;
  num?: number;
  vote_percent?: string | number;
  vote_value?: string;
  width?: number;
  height?: number;
}

export interface IVote {
  id: number;
  gallery_id: string | number;
  vote_title: string;
  begin_at: string;
  end_at: string;
  question_title: string;
  type: number;
  has_voted: number;
  voted_count: number;
  status: number;
  content: IVoteOption[];
  user_voted?: number;
  bool_loop: number;
}

export interface IUserVote {
  id: number;
  vote_id: number;
  vote_value: string;
  num?: number;
}

interface IUserVoteMap {
  [key: string]: IUserVote[] | IUserVote;
}

interface IVoteMap {
  [key: string]: IVote;
}

export default class IMStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  loading: boolean = false;

  @observable list = {} as IVoteMap;
  @observable voted = {} as IUserVoteMap;
  @observable count = {} as {
    [key: string]: {
      total: number;
      options: IVoteOption[];
    };
  };

  @action updateData = (id: string | number, data: any) => {
    let target = this.list[String(id)];
    if (target) {
      this.list[String(id)] = {
        ...target,
        ...data
      };
    }
  };

  @action updateCount = (vote_id: string | number, vote_value: any) => {
    let countData = this.count[String(vote_id)];
    if (!countData) {
      countData = {
        total: 1,
        options: [
          {
            vote_value: vote_value,
            num: 1
          } as IVoteOption
        ]
      };
    } else {
      countData.total += 1;
      let hasProcess = false;
      for (var i = 0; i < countData.options.length; i++) {
        if (countData.options[i]) {
          if (countData.options[i].vote_value == vote_value) {
            hasProcess = true;
            // @ts-ignore
            countData.options[i].num++;
            break;
          }
        }
      }

      if (!hasProcess) {
        countData.options.push({
          vote_value: vote_value,
          num: 1
        } as IVoteOption);
      }
    }

    this.count = {
      ...this.count,
      [String(vote_id)]: countData
    };
  };

  checkVote = (id: string | number, force?: boolean) => {
    return voteApi.one(id, force).then(
      action((res: IResult) => {
        if (res && res.code == 200) {
          let { data } = res;
          if (data && data.id) {
            let { vote_count_detail } = data;
            let total = 0;
            if (vote_count_detail && vote_count_detail.length) {
              for (var i = 0; i < vote_count_detail.length; i++) {
                total += Number(vote_count_detail[i].num);
              }
              this.count = {
                ...this.count,
                [String(data.id)]: {
                  total: total,
                  options: vote_count_detail
                }
              };
            }

            this.list = {
              ...this.list,
              [String(data.id)]: {
                ...data,
                voted_count: total
              }
            };
          }

          return res.data;
        }

        return null;
      })
    );
  };

  checkUserVote = (id: string | number, loop_type: boolean = false) => {
    if (!id) {
      return;
    }

    if(!loop_type) {
      return voteApi.voted([id]).then(
        action((res: IResult) => {
          if (res && res.code == 200) {
            let { data } = res;
            if(data && typeof data == 'object') {
              this.voted = {
                ...this.voted,
                ...data
              };
            }

            return res.data;
          }

          return null;
        })
      );
    }else {
      return voteApi.voteToday(Number(id)).then(action((res:IResult)=>{
        if(res && res.code == 200) {
          let {data} = res;
          this.voted = {
            ...this.voted,
            [String(id)]:{
              id:id,
              vote_id:id,
              vote_value:'',
              num:data
            } as IUserVote
          }
        }
      }))
    }
  };

  getVote = (id: number) => {
    return this.list[String(id)];
  };

  getUserVoted = (id: number) => {
    return this.voted[String(id)];
  };

  isVoteByOption = (id: number, target: string) => {
    let votedData = this.voted[String(id)] as IUserVote[];
    if (votedData && votedData.length) {
      let exist = votedData.find((it: IUserVote) => it.vote_value == target);
      return !!exist;
    }

    return false;
  };

  getOptionCount = (id: number, target: string) => {
    let vote = this.count[String(id)];
    if (vote && vote.total) {
      let num = 0;
      let { options } = vote;
      if (options && options.length) {
        for (var i = 0; i < options.length; i++) {
          if (options[i].vote_value == target) {
            num += Number(options[i].num);
          }
        }

        return {
          count: num,
          percent: ((num * 100) / vote.total).toFixed(2)
        };
      }
    }

    return {
      count: 0,
      percent: 0
    };
  };

  addRecord = (vote_id: number, vote_value: string) => {
    stat.event('vote');
    if(this.loading) {
      return new Promise<IResult>((resolve)=>{
        resolve(null as any);
      });
    }

    this.loading = true;
    return voteApi.addRecord(vote_id, vote_value).then(
      action((res: IResult) => {
        this.loading = false;
        if(res && res.code == 200) {
          // 更新已投票数据

          let vote = this.getVote(vote_id);

          if(vote && Number(vote.bool_loop)) {
            this.voted = {
              ...this.voted,
              [String(vote_id)]:{
                id:vote_id,
                vote_id,
                vote_value:'',
                num:Number(res.data['re-limit'])
              }
            };
          }else {
            let votedData = this.voted[String(vote_id)];
            if (!votedData) {
              votedData = [] as IUserVote[];
            }
            (votedData as IUserVote[]).push({
              ...res.data.record,
              num: 1
            });
            this.voted = {
              ...this.voted,
              [String(vote_id)]: votedData
            };
          }

          // 如果当前没有检测到临时存储的内容，则为当前投票添加一人次的投票人数
          if (!window.sessionStorage.getItem('voteuser_' + vote_id)) {
            let data = this.list[String(vote_id)];
            data.user_voted = (Number(data.user_voted) || 0) + 1;
            this.list[String(vote_id)] = data;

            window.sessionStorage.setItem('voteuser_' + vote_id, '1');
          }
        }

        return res.data;
      })
    ).catch(()=>{
      this.loading = false;
    });
  };
}
